<script lang="ts">
  import { adminModuleCss } from "../lib/gruimModuleTheme";
  import Progressbar from "./progressbar.svelte";
  import MessageModal from "./messageModal.svelte";
  import { onDestroy, onMount } from "svelte";
  import Toasts from "./toast/Toasts.svelte";
  import { addToast } from "./toast/store";
  import { opportunityOptions } from "../lib/opportunityOptionsStore";
  import { eventBus } from "../lib/eventBus";
  import { installOpportunityFetchNormalizer } from "../lib/helper";
  import { syncOpportunityToHubspotById } from "../lib/hubspotAuth";
  import { isAwsCatalog } from "../lib/utils";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";
  import { buildMandatoryFieldFillMessage, getAiFailureMessage } from "../lib/mandatoryFieldFillUi";
  import { destroySvelteComponentInstance, mountFederatedSvelteComponent } from "../lib/mountFederatedSvelteComponent";
  import { createStatusAwareReadonlyFields } from "../lib/awsOpportunityUpdateRules";

  const DEFAULT_URL = process.env.SVELTE_APP_REMOTE_URL;
  const SETTINGS_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/hubspot-config/settings`;
  const AWS_WRITABLE_REVIEW_STATUSES = new Set(["Pending Submission", "Submitted"]);

  const sanitizeMandatoryFieldContextProperties = (row: Record<string, any>): Record<string, any> => {
    const excluded = new Set([
      "recommendation",
      "recommendation_v2",
      "mandatoryFieldFillLog",
      "aws_mandatory_field_fill_log_v2",
      "nextStepsHistory",
      "next_steps_history_v2",
      "ai_chatbot_input_v2",
      "ai_chatbot_output_v2",
    ]);
    return Object.fromEntries(
      Object.entries(row).filter(([key, value]) => {
        if (excluded.has(key)) return false;
        if (value === undefined || value === null || value === "") return false;
        return true;
      })
    );
  };
  
  let el1: HTMLDivElement;

  let total = 0;
  let completed = 0;
  let showProgressBar = false;

  let showSyncModal = false;
  let syncModalTitle = "Sync Result";
  let syncMessage: string = "";
  let syncDetails: Record<string, unknown> | null = null;
  let refreshAfterSyncModalClose = false;

  let showDeleteModal = false;
  let deleteMessage = "";
  let deleteDetails: Record<string, unknown> | null = null;

  let showCloseModal = false;
  let closeMessage = "";
  let closeDetails: Record<string, unknown> | null = null;
  let awsCatalog = "sandbox";

  let showRecommendationModal = false;
  let recommendationLoading = false;
  let recommendationModalTitle = "Recommendation";
  let recommendationMessage = "";
  let recommendationText = "";
  let recommendationDownloadBody: string | null = null;
  let recommendationLogFileName: string | null = null;
  
  let restoreOpportunityFetch: (() => void) | null = null;
  let opportunitiesInstance: any = null;

  type CopyToastPayload = { text?: string };

  const renderCopyableCell = (value: unknown): string => {
    const text = String(value ?? "").trim();
    if (!text) return "";

    const encoded = encodeURIComponent(text);

    return `<span style="display:inline-flex;align-items:center;vertical-align:middle;"><button type="button" data-copy-value="${encoded}" title="Copy" aria-label="Copy" style="margin-right:8px;border:0;background:transparent;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;padding:0;color:#111827;font-size:16px;line-height:1;">📋</button><span style="vertical-align:middle;">${text}</span></span>`;
  };

  const resolveFieldValue = (
    value: unknown,
    row: Record<string, unknown> | undefined,
    key: string
  ): unknown =>
    value ?? row?.[key] ?? row?.[`${key}Id`];

  const formatHistoryTime = (raw: string): string => {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
  };

  const toJsonArrayString = (value: unknown): string => {
    const tryParse = (v: unknown): any => {
      if (typeof v !== "string") return v;
      try {
        return JSON.parse(v);
      } catch {
        return v;
      }
    };

    let parsed = tryParse(value);

    if (Array.isArray(parsed) && parsed.every((c) => typeof c === "string" && c.length === 1)) {
      parsed = tryParse(parsed.join(""));
    }

    if (!Array.isArray(parsed)) return "[]";

    const result = parsed
      .map(tryParse)
      .map((e) => {
        if (e && typeof e === "object") {
          const time = typeof e.Time === "string" ? formatHistoryTime(e.Time) : "";
          const val = e.Value == null ? "" : typeof e.Value === "string" ? e.Value : String(e.Value);

          return [time, val].filter(Boolean).join(": ");
        }

        return e == null ? "" : String(e);
      })
      .filter(Boolean);

    return JSON.stringify(result);
  };

  const closeModal = (): void => {
    showSyncModal = false;
    syncModalTitle = "Sync Result";
    syncMessage = "";
    syncDetails = null;
    if (refreshAfterSyncModalClose) {
      refreshAfterSyncModalClose = false;
      triggerOpportunityDataRefresh();
    }
  };

  const showCopyToast = ({ text }: CopyToastPayload = {}): void => {
    const copiedText = String(text ?? "").trim();
    const copiedLabel = copiedText ? `"${copiedText}"` : "value";

    addToast({
      message: `Copied to clipboard: ${copiedLabel}`,
      type: "success",
      dismissible: false,
      timeout: 1600,
    });
  };

  const fallbackCopyText = (text: string): boolean => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

const handleCopyCell = async (encodedValue: string): Promise<void> => {
    const copiedText = decodeURIComponent(encodedValue);
    // void navigator.clipboard?.writeText(copiedText).catch(() => undefined);
    // showCopyToast({ text: copiedText });
      try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(copiedText);
    } else {
      const success = fallbackCopyText(copiedText);
      if (!success) {
        throw new Error("execCommand copy failed");
      }
    }
    showCopyToast({ text: copiedText });
  } catch (err) {
    console.error("Copy failed:", err);
    addToast({
      message: "Copy failed. Please copy manually.",
      type: "error",
      dismissible: true,
      timeout: 3000,
    });
  }
  };

  const resolveCopyButton = (target: EventTarget | null): HTMLButtonElement | null => {
    if (!(target instanceof Element)) return null;
    return target.closest("[data-copy-value]") as HTMLButtonElement | null;
  };

  const handleCopyClick = (event: MouseEvent): void => {
    const button = resolveCopyButton(event.target);
    const encodedValue = button?.dataset.copyValue;
    if (!encodedValue) return;

    event.preventDefault();
    event.stopPropagation();
    void handleCopyCell(encodedValue);
  };

  const fetchAwsCatalog = async (): Promise<void> => {
    try {
      const res = await fetch(SETTINGS_URL);
      if (!res.ok) throw new Error("Failed to load AWS catalog settings");
      const data = await res.json();
      awsCatalog = data?.awsCatalog || "sandbox";
    } catch (err) {
      console.error("Failed to load AWS catalog settings", err);
      awsCatalog = "sandbox";
    }
  };

  const deleteOpportunitiesWithRelations = async (ids: number[], showProgress = true) => {
    if (!ids.length) {
      return { success: true, results: [] as { id: number; data: any }[] };
    }

    if (showProgress) {
      showProgressBar = true;
      total = ids.length;
      completed = 0;
    }

    const results: { id: number; data: any }[] = [];

    try {
      for (const id of ids) {
        try {
          const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunities/delete-wr`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ids: [id] }),
          });
          results.push({ id, data: await res.json() });
        } catch (err: any) {
          results.push({ id, data: { success: false, error: err?.message || err } });
        } finally {
          if (showProgress) {
            completed++;
          }
        }
      }

      const success = results.every((r) => r.data?.success !== false);
      const stage = results.find((r) => r.data?.stage)?.data?.stage;

      return { success, results, stage };
    } finally {
      if (showProgress) {
        completed = total;
        showProgressBar = false;
      }
      triggerOpportunityDataRefresh();
    }
  };


  const closeOpportunities = async (ids: number[], showProgress = true) => {
    if (!ids.length) {
      return { success: true, results: [] as { id: number; data: any }[] };
    }

    if (showProgress) {
      showProgressBar = true;
      total = ids.length;
      completed = 0;
    }

    const results: { id: number; data: any }[] = [];

    try {
      for (const id of ids) {
        try {
          const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunities/close`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ids: [id] }),
          });
          results.push({ id, data: await res.json() });
        } catch (err: any) {
          results.push({ id, data: { success: false, error: err?.message || err } });
        } finally {
          if (showProgress) {
            completed++;
          }
        }
      }

      const success = results.every((r) => r.data?.success !== false);
      return { success, results };
    } finally {
      if (showProgress) {
        completed = total;
        showProgressBar = false;
      }
      triggerOpportunityDataRefresh();
    }
  };

  const mountOpportunities = async () => {
    const { default: Opportunities } = await import("App/alaceOpportunities");

    if (el1) {
      opportunitiesInstance = mountFederatedSvelteComponent(opportunitiesInstance, Opportunities, {
        target: el1,
        props: {
          css: adminModuleCss,
          translation: {
            opportunityType: "Opportunity Type",
            targetCloseDate: "Target Close Date",
            partnerCrmUnique: "Partner CRM Unique ID",
            partnerCrmUniqueId: "Partner CRM Unique ID",
            awsOpp: "AWS Opportunity ID",
            awsOppId: "AWS Opportunity ID",
            parentOpp: "Parent Opportunity ID",
            syncToAws: "Sync to AWS",
            lastSyncedToAws: "Last Synced to AWS",
            doesOppBelongToNatSec: "Does Opportunity Belong to National Security?",
            opportunityCustomer: "Customer Company Name",
            primaryNeedsFromAws: "Partner Primary Needs From AWS",
            parentOppId: "Parent Opportunity ID",
            closed: "Closed",
            awsStage: "AWS Stage",
            reviewStatus: "AWS Review Status",
            closedLostReason: "Closed Lost Reason",
            nextSteps: "Next Steps",
            nextStepsHistory: "Next Steps History",
            recommendation: "Recommendation",
            dealName: "Deal Name",
            partnerProjectTitle: "Project Title",
            awsProducts: "AWS Products",
            awsProductIds: "AWS Products",
            address: "Address",
            addressId: "Address",
          },
          quickFilters: [
            { label: "Closed Opportunities", filter: { and: [{ closed: "1" }] } },
            { label: "Open Opportunities", filter: { and: [{ closed: "0" }] } },
          ],
          schema: {
            actions: {
              edit: { removable: true, "with-confirmation": true },
            },
            "field-properties": {
              "hidden-fields": [
                "projectId",
                "partnerProjectTitle",
                "customerId",
                "syncToAws",
                "origin",
                "fundingAssessment",
              ],
              "auto-generated-fields": [
                "awsOpp",
                "partnerCrmUnique",
                "awsOppId",
                "partnerCrmUniqueId",
                "lastSyncedToAws",
                "id",
                "awsStage",
                "nextStepsHistory",
              ],
              "readonly-fields": createStatusAwareReadonlyFields([
                "awsOpp",
                "partnerCrmUnique",
                "awsOppId",
                "partnerCrmUniqueId",
                "recommendation",
              ]),
              "field-order": [
                "awsOpp",
                "partnerCrmUnique",
                "dealName",
                "partnerProjectTitle",
                "recommendation",
                "opportunityCustomer",
                "awsProducts",
                "awsProductIds",
                "awsStage",
                "addressId",
              ],
              "date-fields": [
                { name: "targetCloseDate", type: "date" },
                { name: "lastSyncedToAws", type: "date" },
              ],
              "field-transformation": [
                {
                  name: "awsOpp",
                  transformation: (value: unknown, row: Record<string, unknown>) =>
                    renderCopyableCell(resolveFieldValue(value, row, "awsOppId")),
                },
                {
                  name: "partnerCrmUnique",
                  transformation: (value: unknown, row: Record<string, unknown>) =>
                    renderCopyableCell(resolveFieldValue(value, row, "partnerCrmUniqueId")),
                },
                {
                  name: "dealName",
                  transformation: (value: unknown, row: Record<string, unknown>) =>
                    (resolveFieldValue(value, row, "dealName") ||
                      (row?.project as { partnerProjectTitle?: string } | undefined)
                        ?.partnerProjectTitle ||
                      "") as string,
                },
                {
                  name: "partnerProjectTitle",
                  transformation: (value: unknown, row: Record<string, unknown>) =>
                    (resolveFieldValue(value, row, "partnerProjectTitle") ||
                      (row?.project as { partnerProjectTitle?: string } | undefined)
                        ?.partnerProjectTitle ||
                      "") as string,
                },
                {
                  name: "nextStepsHistory",
                  transformation: (value: unknown, row: Record<string, unknown>) =>
                    toJsonArrayString(resolveFieldValue(value, row, "nextStepsHistory")),
                },
              ],
              "dropdown-fields": [
                {
                  name: "closed",
                  default: { label: "Open", value: "0" },
                  options: [
                    { label: "Open", value: "0" },
                    { label: "Closed", value: "1" },
                  ],
                },
                {
                  name: "doesOppBelongToNatSec",
                  default: { label: "No", value: "No" },
                  options: (options?.nationalSecurityFlag ?? []).map((v) => ({
                    label: v,
                    value: v,
                  })),
                },
                {
                  name: "opportunityType",
                  options: (options?.opportunityType ?? []).map((v) => ({ label: v, value: v })),
                },
                {
                  name: "primaryNeedsFromAws",
                  options: (options?.primaryNeedsFromAws ?? []).map((v) => ({
                    label: v,
                    value: v,
                  })),
                },
                {
                  name: "closedLostReason",
                  options: (options?.closedLostReason ?? []).map((v) => ({
                    label: v,
                    value: v,
                  })),
                },
                {
                  name: "reviewStatus",
                  options: reviewStatusOptions,
                },
              ],
              "textarea-fields": ["nextSteps", "recommendation"],
              "relational-fields": [
                {
                  name: "awsProductIds",
                  editable: true,
                  columns: ["product"],
                  useFuzzySearch: true,
                },
                {
                  name: "addressId",
                  editable: true,
                  columns: ["streetAddress", "city", "state", "postalCode", "country"],
                  useFuzzySearch: true,
                },
              ],
              "json-fields": [
                { field: "nextStepsHistory", template: "${0}" },
              ],
            },
            "custom-bulk-actions": [
              {
                action: "Close",
                bulkFn: async (ids: number[]) => {
                  if (!confirm(`Are you sure you want to close ${ids.length} opportunities?`)) {
                    return { cancelled: true };
                  }
                  return closeOpportunities(ids);
                },
                singleFn: async (row: { id: number }) => {
                  const data = await closeOpportunities([row.id], false);
                  const firstResult = data.results?.[0]?.data ?? {};

                  closeMessage = data.success
                    ? `Opportunity ${row.id} was closed successfully.`
                    : `Failed to close opportunity ${row.id}.`;

                  closeDetails = firstResult.response || firstResult;
                  showCloseModal = true;

                  return data;
                },
              },
              {
                action: "Sync to AWS",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results = [];
                  for (const id of ids) {
                    const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunity/${id}`, {
                      method: "POST",
                    });
                    results.push(await res.json());
                    completed++;
                  }

                  showProgressBar = false;
                  if (results.some((result) => result?.success !== false)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number }) => {
                  const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunity/${row.id}`, {
                    method: "POST",
                  });

                  const data = await res.json();

                  if (data.success) {
                    syncModalTitle = "Sync Result";
                    syncMessage = `Opportunity ${data.opportunityId} synced to AWS (AWS Opportunity ID: ${data.awsOppId})`;
                  } else {
                    syncModalTitle = "Sync Result";
                    syncMessage = "Failed to sync opportunity.";
                  }

                  syncDetails = data.response || {};
                  showSyncModal = true;
                  if (data.success) {
                    refreshAfterSyncModalClose = true;
                  }

                  return data;
                },
              },
              {
                action: "Sync to HubSpot",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results = [];
                  try {
                    for (const id of ids) {
                      try {
                        const result = await syncOpportunityToHubspotById(id);
                        results.push(result);

                        if (result.authRequired) {
                          break;
                        }
                      } finally {
                        completed++;
                      }
                    }
                  } finally {
                    showProgressBar = false;
                  }
                  if (results.some((result) => result?.success)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number }) => {
                  const result = await syncOpportunityToHubspotById(row.id, {
                    fallbackMessage: "Failed to sync opportunity to HubSpot.",
                  });

                  if (result.authRequired) {
                    return result;
                  }

                  if (!result.success) {
                    syncModalTitle = "Sync Result";
                    syncMessage = result.message || "Failed to sync opportunity to HubSpot.";
                  } else if ("hubspotId" in result) {
                    syncModalTitle = "Sync Result";
                    syncMessage = `Opportunity ${row.id} synced to HubSpot (HubSpot ID: ${result.hubspotId})`;
                  } else {
                    syncModalTitle = "Sync Result";
                    syncMessage = "Failed to sync opportunity to HubSpot.";
                  }

                  syncDetails = result;
                  showSyncModal = true;
                  if (result.success) {
                    refreshAfterSyncModalClose = true;
                  }

                  return result;
                },
              },
              {
                action: "Validate Opportunity",
                singleFn: async (row: { id: number }) => {
                  const localRes = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunity/local/${row.id}`);
                  const localData = await localRes.json();
                  const payload = localData?.payload;
                  if (!localRes.ok || localData?.success === false || !payload) {
                    const result = {
                      success: false,
                      opportunityId: row.id,
                      error: localData?.response?.error || `Failed to load opportunity ${row.id}.`,
                    };
                    syncModalTitle = "Validation Result";
                    syncMessage = result.error;
                    syncDetails = result;
                    showSyncModal = true;
                    return result;
                  }
                  const res = await fetch(`${DEFAULT_URL}/api/hubspot-sync/opportunity/validate`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                  });
                  const result = await res.json();

                  syncModalTitle = "Opportunity Validation";
                  syncMessage = "";
                  syncDetails = result ?? {};
                  showSyncModal = true;

                  return result;
                },
              },
              {
                action: "Delete with Relations",
                bulkFn: async (ids: number[]) => {
                  if (
                    !confirm(
                      `Are you sure you want to delete ${ids.length} opportunities and all related data?`
                    )
                  ) {
                    return { cancelled: true };
                  }
                  return deleteOpportunitiesWithRelations(ids);
                },
                singleFn: async (row: { id: number }) => {
                  const data = await deleteOpportunitiesWithRelations([row.id], false);
                  const firstResult = data.results?.[0]?.data ?? {};

                  deleteMessage = data.success
                    ? `Opportunity ${row.id} and related records were deleted successfully.`
                    : `Failed to delete opportunity ${row.id}.`;

                  deleteDetails = firstResult.responses?.[row.id] || firstResult;
                  showDeleteModal = true;

                  return data;
                },
              },
              {
                action: "Edit Opportunity",
                singleFn: async (row: { id: number }) => {
                  eventBus.emit("open-opportunity-popup", { id: row.id });
                  return { success: true };
                },
              },
              {
                action: "Generate Recommendation",
                singleFn: async (row: { id: number }) => {
                  recommendationModalTitle = "Recommendation";
                  recommendationMessage = "Generating recommendation…";
                  recommendationLoading = true;
                  showRecommendationModal = true;

                  try {
                    const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunity/${row.id}/recommendation`, {
                      method: "POST",
                    });
                    const data = await res.json();

                    if (!data.success && data.message) console.error("[recommendation]", data.message);

                    if (data.recommendation === "Recommendation task published to Dapr successfully.") {
                      recommendationMessage = "Generating recommendation…";
                      recommendationLoading = true;

                      const poll = async () => {
                        if (!showRecommendationModal) return;
                        try {
                          const statusRes = await fetch(
                            `${DEFAULT_URL}/api/alace/sync/opportunity/${row.id}/recommendation/status`
                          );
                          const statusData = await statusRes.json();
                          if (statusData.status === "success") {
                            recommendationMessage = `Recommendation generated for opportunity ${row.id}.`;
                            recommendationText = statusData.result?.recommendation || "";
                            recommendationLoading = false;
                          } else if (statusData.status === "failed") {
                            recommendationMessage = `Failed to generate recommendation for opportunity ${row.id}: ${statusData.error || ""}`;
                            recommendationLoading = false;
                          } else {
                            setTimeout(poll, 2000);
                          }
                        } catch (err) {
                          recommendationMessage = "Error polling recommendation status.";
                          recommendationLoading = false;
                        }
                      };
                      setTimeout(poll, 2000);
                    } else {
                      recommendationMessage = data.message || (
                        data.success
                          ? `Recommendation generated for opportunity ${data.opportunityId || row.id}.`
                          : `Failed to generate recommendation for opportunity ${row.id}.`
                      );
                      recommendationText = data.recommendation || "";
                      recommendationDownloadBody = null;
                      recommendationLogFileName = null;
                      recommendationLoading = false;
                    }

                    return data;
                  } catch (e) {
                    recommendationMessage = `Failed to generate recommendation for opportunity ${row.id}.`;
                    recommendationLoading = false;
                    throw e;
                  }
                },
              },
              {
                action: "Fill AWS Mandatory Fields",
                singleFn: async (row: { id: number; mandatoryFieldContext?: string; aws_mandatory_field_context_v2?: string; [key: string]: any }) => {
                  recommendationModalTitle = "AWS mandatory field fill";
                  recommendationMessage = "Filling AWS mandatory fields…";
                  recommendationLoading = true;
                  showRecommendationModal = true;

                  try {
                    const context = JSON.stringify({
                      context: row.mandatoryFieldContext || row.aws_mandatory_field_context_v2 || undefined,
                      hubspotDealProperties: sanitizeMandatoryFieldContextProperties(row),
                    });
                    const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunity/${row.id}/mandatory-field-fill`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ dryRun: false, context }),
                    });
                    const data = await res.json();

                    if (data.log === "Mandatory field fill task published to Dapr successfully.") {
                      recommendationMessage = "Filling AWS mandatory fields…";
                      recommendationLoading = true;

                      const poll = async () => {
                        if (!showRecommendationModal) return;
                        try {
                          const statusRes = await fetch(
                            `${DEFAULT_URL}/api/alace/sync/opportunity/${row.id}/mandatory-field-fill/status`
                          );
                          const statusData = await statusRes.json();
                          if (statusData.status === "success") {
                            recommendationMessage = statusData.result?.message || getAiFailureMessage(statusData.result?.unresolved) || (
                              statusData.result?.success
                                ? buildMandatoryFieldFillMessage(statusData.result, (row as any).awsOppId)
                                : "AWS mandatory fields filled successfully."
                            );
                            recommendationText = "";
                            recommendationDownloadBody = statusData.result?.log || null;
                            recommendationLogFileName = `aws-mandatory-field-fill-${row.id}.log`;
                            recommendationLoading = false;
                          } else if (statusData.status === "failed") {
                            recommendationMessage = `Failed to fill AWS mandatory fields for opportunity ${row.id}: ${statusData.error || ""}`;
                            recommendationLoading = false;
                          } else {
                            setTimeout(poll, 2000);
                          }
                        } catch (err) {
                          recommendationMessage = "Error polling mandatory field fill status.";
                          recommendationLoading = false;
                        }
                      };
                      setTimeout(poll, 2000);
                    } else {
                      recommendationMessage = data.message || getAiFailureMessage(data.unresolved) || (
                        data.success
                          ? buildMandatoryFieldFillMessage(data, (row as any).awsOppId)
                          : `Failed to fill AWS mandatory fields for opportunity ${row.id}.`
                      );
                      recommendationText = "";
                      recommendationDownloadBody = data.log || null;
                      recommendationLogFileName = data.log && data.logFileName ? data.logFileName : null;
                      recommendationLoading = false;
                    }

                    return data;
                  } catch (e) {
                    recommendationMessage = `Failed to fill AWS mandatory fields for opportunity ${row.id}.`;
                    recommendationLoading = false;
                    throw e;
                  }
                },
              },
            ],
          },
        },
      });
    }
  };

  onMount(async () => {
    el1?.addEventListener("click", handleCopyClick, true);
    restoreOpportunityFetch = installOpportunityFetchNormalizer();
    await Promise.all([opportunityOptions.fetchOptions(), fetchAwsCatalog()]);
    void mountOpportunities();
  });

  onDestroy(() => {
    opportunitiesInstance = destroySvelteComponentInstance(opportunitiesInstance);
    el1?.removeEventListener("click", handleCopyClick, true);
    restoreOpportunityFetch?.();
    restoreOpportunityFetch = null;
  });

  $: options = $opportunityOptions.data;
  $: reviewStatusOptions = (options?.reviewStatus ?? [])
    .filter((status) => !isAwsCatalog(awsCatalog) || AWS_WRITABLE_REVIEW_STATUSES.has(status))
    .map((status) => ({ label: status, value: status }));
</script>

{#if showProgressBar}
  <Progressbar {completed} {total} />
{/if}

<div bind:this={el1} />

<MessageModal
  show={showSyncModal}
  title={syncModalTitle}
  message={syncMessage}
  details={syncDetails}
  onClose={closeModal}
/>

<MessageModal
  show={showDeleteModal}
  title="Delete Result"
  message={deleteMessage}
  details={deleteDetails}
  onClose={() => (showDeleteModal = false)}
/>

<MessageModal
  show={showCloseModal}
  title="Close Result"
  message={closeMessage}
  details={closeDetails}
  onClose={() => (showCloseModal = false)}
/>

<MessageModal
  show={showRecommendationModal}
  loading={recommendationLoading}
  title={recommendationModalTitle}
  message={recommendationMessage}
  body={recommendationText}
  downloadBody={recommendationDownloadBody}
  downloadFileName={recommendationLogFileName}
  onClose={() => {
    showRecommendationModal = false;
    recommendationText = "";
    recommendationDownloadBody = null;
    recommendationLogFileName = null;
  }}
/>

<Toasts />
